import { useEffect, useMemo, useRef, useState } from "react";
import { AppIcon, MetricCard } from "../components/InstateamMasterComponents";
import {
  createAdminLeaveRequest,
  getAdminLeavesOverview,
  importAdminLeaves,
  reviewAdminLeaveRequest,
} from "../api/instateamLeavesAdminApi";
import {
  ADMIN_LEAVE_DURATION_OPTIONS,
  ADMIN_LEAVE_STATUS_OPTIONS,
  createLeavesDonutStyle,
  downloadLeavesCsv,
  formatLeaveDays,
  formatMonthHeading,
  getAdminLeaveTypeMeta,
  getCurrentMonthKey,
  getLeaveStatusMeta,
  parseLeavesCsv,
} from "../utils/adminLeavesHelpers";
import { getInitials, toAbsoluteUrl } from "../utils/adminHelpers";

const PAGE_SIZE_OPTIONS = [5, 10, 20];

const INITIAL_APPLY_FORM = {
  employeeId: "",
  leaveType: "casual_leave",
  duration: "full_day",
  fromDateKey: "",
  toDateKey: "",
  reason: "",
  status: "approved",
};

const EMPTY_ADMIN_LEAVE_METRICS = {
  totalRequests: 0,
  pendingRequests: 0,
  approvedRequests: 0,
  rejectedRequests: 0,
  employeesOnLeaveToday: 0,
};

function Avatar({ leave }) {
  if (leave?.profilePhoto?.url) {
    return (
      <img
        src={toAbsoluteUrl(leave.profilePhoto.url)}
        alt={leave.fullName}
        className="attendance-person-avatar"
      />
    );
  }

  return (
    <div className="avatar-circle small leave-avatar-fallback">
      {getInitials(leave?.fullName)}
    </div>
  );
}

function LeavePolicyModal({ policy, onClose }) {
  if (!policy) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="department-modal leaves-policy-modal">
        <div className="employee-modal-header">
          <div className="employee-modal-title">
            <h3>Leave Policy</h3>
            <p>{policy.note}</p>
          </div>

          <button className="icon-button" type="button" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="leaves-policy-grid">
          {policy.leaveTypes?.map((leaveType) => (
            <div key={leaveType.key} className="leaves-policy-item">
              <div className={`attendance-quick-icon ${leaveType.accent || "warning"}`}>
                <AppIcon name={getAdminLeaveTypeMeta(leaveType.key).icon} />
              </div>
              <div>
                <strong>{leaveType.label}</strong>
                <span>
                  {leaveType.dynamicAllocation
                    ? "Earned after approved comp off requests"
                    : `${leaveType.allocation} days allocated`}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ApplyLeaveModal({
  employees,
  leaveTypes,
  form,
  onChange,
  onClose,
  onSubmit,
  isSubmitting,
}) {
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="department-modal leaves-apply-modal">
        <div className="employee-modal-header">
          <div className="employee-modal-title">
            <h3>Apply Leave For Employee</h3>
            <p>Create a leave entry directly from the admin panel.</p>
          </div>

          <button className="icon-button" type="button" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <form className="department-modal-form" onSubmit={onSubmit}>
          <div className="attendance-form-grid leaves-apply-grid">
            <label className="form-field">
              <span>Employee</span>
              <div className="field-shell select-shell">
                <select
                  value={form.employeeId}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      employeeId: event.target.value,
                    }))
                  }
                >
                  <option value="">Select employee</option>
                  {employees.map((employee) => (
                    <option key={employee._id} value={employee._id}>
                      {employee.fullName} ({employee.employeeId})
                    </option>
                  ))}
                </select>
                <AppIcon name="chevronDown" />
              </div>
            </label>

            <label className="form-field">
              <span>Leave Type</span>
              <div className="field-shell select-shell">
                <select
                  value={form.leaveType}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      leaveType: event.target.value,
                    }))
                  }
                >
                  {leaveTypes.map((leaveType) => (
                    <option key={leaveType.key} value={leaveType.key}>
                      {leaveType.label}
                    </option>
                  ))}
                </select>
                <AppIcon name="chevronDown" />
              </div>
            </label>

            <label className="form-field">
              <span>Duration</span>
              <div className="field-shell select-shell">
                <select
                  value={form.duration}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      duration: event.target.value,
                      toDateKey:
                        event.target.value === "multiple_days"
                          ? current.toDateKey
                          : current.fromDateKey,
                    }))
                  }
                >
                  {ADMIN_LEAVE_DURATION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <AppIcon name="chevronDown" />
              </div>
            </label>

            <label className="form-field">
              <span>Status</span>
              <div className="field-shell select-shell">
                <select
                  value={form.status}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      status: event.target.value,
                    }))
                  }
                >
                  {ADMIN_LEAVE_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <AppIcon name="chevronDown" />
              </div>
            </label>

            <label className="form-field">
              <span>From Date</span>
              <div className="field-shell">
                <input
                  type="date"
                  value={form.fromDateKey}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      fromDateKey: event.target.value,
                      toDateKey:
                        current.duration === "multiple_days"
                          ? current.toDateKey
                          : event.target.value,
                    }))
                  }
                />
                <AppIcon name="calendar" />
              </div>
            </label>

            <label className="form-field">
              <span>To Date</span>
              <div className="field-shell">
                <input
                  type="date"
                  value={form.duration === "multiple_days" ? form.toDateKey : form.fromDateKey}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      toDateKey: event.target.value,
                    }))
                  }
                  disabled={form.duration !== "multiple_days"}
                />
                <AppIcon name="calendar" />
              </div>
            </label>

            <label className="form-field leaves-apply-reason-field">
              <span>Reason</span>
              <div className="textarea-shell leaves-textarea-shell">
                <textarea
                  value={form.reason}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      reason: event.target.value,
                    }))
                  }
                  placeholder="Enter the leave reason..."
                />
              </div>
            </label>
          </div>

          <div className="form-footer">
            <span className="required-note">
              Approved requests will immediately reflect in attendance as on leave.
            </span>
            <div className="form-footer-actions">
              <button className="secondary-button" type="button" onClick={onClose}>
                Cancel
              </button>
              <button className="primary-button save-button" type="submit">
                {isSubmitting ? "Saving..." : "Save Leave"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function ReviewLeaveModal({
  leave,
  reviewNote,
  onReviewNoteChange,
  onApprove,
  onReject,
  onClose,
  isSubmitting,
}) {
  if (!leave) return null;

  const statusMeta = getLeaveStatusMeta(leave.status);
  const typeMeta = getAdminLeaveTypeMeta(leave.leaveType);

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="employee-modal leave-review-modal">
        <div className="employee-modal-header">
          <div className="employee-modal-title">
            <h3>{leave.leaveTypeLabel}</h3>
            <p>Review the request details and update the final decision.</p>
          </div>

          <button className="icon-button" type="button" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="employee-modal-hero">
          <div className={`attendance-quick-icon ${typeMeta.accent || "warning"}`}>
            <AppIcon name={typeMeta.icon} />
          </div>
          <div>
            <strong>{leave.fullName}</strong>
            <p>
              {leave.employeeId} • {leave.designation} • {leave.department}
            </p>
          </div>
          <span className={`status-badge ${statusMeta.tone}`}>{statusMeta.label}</span>
        </div>

        <div className="employee-modal-grid leave-review-grid">
          <div className="info-block">
            <span>Duration</span>
            <strong>{leave.durationLabel}</strong>
          </div>
          <div className="info-block">
            <span>Date Range</span>
            <strong>
              {leave.fromDateLabel} - {leave.toDateLabel}
            </strong>
          </div>
          <div className="info-block">
            <span>Total Days</span>
            <strong>{formatLeaveDays(leave.totalDays)}</strong>
          </div>
          <div className="info-block">
            <span>Applied On</span>
            <strong>{leave.appliedOnLabel}</strong>
          </div>
        </div>

        <div className="employee-documents leave-review-copy-card">
          <strong>Reason</strong>
          <p>{leave.reason || "No reason was provided."}</p>
        </div>

        <label className="form-field">
          <span>Review Note</span>
          <div className="textarea-shell leaves-textarea-shell">
            <textarea
              value={reviewNote}
              onChange={(event) => onReviewNoteChange(event.target.value)}
              placeholder="Add an optional note for this decision..."
            />
          </div>
        </label>

        <div className="form-footer-actions leaves-review-actions">
          <button className="secondary-button" type="button" onClick={onClose}>
            Close
          </button>
          {leave.status === "pending" ? (
            <>
              <button
                className="secondary-button leave-reject-button"
                type="button"
                onClick={onReject}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Updating..." : "Reject"}
              </button>
              <button
                className="primary-button save-button"
                type="button"
                onClick={onApprove}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Updating..." : "Approve"}
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function LeavesPage({
  token,
  employees,
  departmentOptions,
  searchText = "",
  onProtectedError,
  onNotify,
}) {
  const [monthKey, setMonthKey] = useState(getCurrentMonthKey);
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [leaveTypeFilter, setLeaveTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [overview, setOverview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [reviewNote, setReviewNote] = useState("");
  const [applyForm, setApplyForm] = useState(INITIAL_APPLY_FORM);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [departmentFilter, leaveTypeFilter, searchText, statusFilter, pageSize]);

  useEffect(() => {
    let isActive = true;

    async function fetchOverview() {
      try {
        setIsLoading(true);
        const payload = await getAdminLeavesOverview(token, {
          month: monthKey,
          department: departmentFilter,
          leaveType: leaveTypeFilter,
          status: statusFilter,
        });

        if (!isActive) return;
        setOverview(payload.overview || null);
      } catch (error) {
        if (!isActive) return;
        onNotify?.({
          type: "error",
          text: error.message,
        });
        onProtectedError?.(error);
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    }

    fetchOverview();

    return () => {
      isActive = false;
    };
  }, [
    departmentFilter,
    leaveTypeFilter,
    monthKey,
    onNotify,
    onProtectedError,
    statusFilter,
    token,
  ]);

  const leaveTypes = overview?.policy?.leaveTypes || [];
  const metrics = overview?.metrics || EMPTY_ADMIN_LEAVE_METRICS;
  const statusBreakdown = overview?.statusBreakdown || metrics;
  const requests = useMemo(() => {
    const query = String(searchText || "").trim().toLowerCase();
    const rows = overview?.requests || [];
    if (!query) return rows;
    return rows.filter((leave) =>
      [
        leave.fullName,
        leave.employeeId,
        leave.department,
        leave.designation,
        leave.leaveTypeLabel,
        leave.leaveType,
        leave.status,
        leave.fromDateKey,
        leave.toDateKey,
        leave.reason,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [overview?.requests, searchText]);
  const totalPages = Math.max(1, Math.ceil(requests.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedRequests = requests.slice((safePage - 1) * pageSize, safePage * pageSize);
  const pendingRequests = overview?.pendingRequests || [];
  const leaveTypeSummary = overview?.leaveTypeSummary || [];
  const donutStyle = useMemo(
    () => ({ background: createLeavesDonutStyle(metrics) }),
    [metrics]
  );
  const activeEmployees = useMemo(
    () => (employees || []).filter((employee) => employee.status === "active"),
    [employees]
  );

  async function refreshOverview(nextStatus = statusFilter) {
    const payload = await getAdminLeavesOverview(token, {
      month: monthKey,
      department: departmentFilter,
      leaveType: leaveTypeFilter,
      status: nextStatus,
    });
    setOverview(payload.overview || null);
  }

  function openApplyLeaveModal() {
    const today = new Date();
    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(today.getDate()).padStart(2, "0")}`;

    setApplyForm({
      ...INITIAL_APPLY_FORM,
      employeeId: activeEmployees[0]?._id || "",
      leaveType: leaveTypes[0]?.key || "casual_leave",
      fromDateKey: todayKey,
      toDateKey: todayKey,
    });
    setShowApplyModal(true);
  }

  function openReviewModal(leave) {
    setSelectedLeave(leave);
    setReviewNote(leave.reviewNote || "");
  }

  async function handleSubmitApplyLeave(event) {
    event.preventDefault();

    if (!applyForm.employeeId || !applyForm.fromDateKey || !applyForm.reason.trim()) {
      onNotify?.({
        type: "warning",
        text: "Choose an employee, dates, and the reason first.",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      await createAdminLeaveRequest(token, {
        employeeId: applyForm.employeeId,
        leaveType: applyForm.leaveType,
        duration: applyForm.duration,
        fromDateKey: applyForm.fromDateKey,
        toDateKey:
          applyForm.duration === "multiple_days"
            ? applyForm.toDateKey
            : applyForm.fromDateKey,
        reason: applyForm.reason,
        status: applyForm.status,
      });

      setShowApplyModal(false);
      await refreshOverview();
      onNotify?.({
        type: "success",
        text: "Leave request saved successfully.",
      });
    } catch (error) {
      onNotify?.({
        type: "error",
        text: error.message,
      });
      onProtectedError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleReviewLeave(decision) {
    if (!selectedLeave?._id) return;

    try {
      setIsSubmitting(true);

      await reviewAdminLeaveRequest(token, selectedLeave._id, {
        decision,
        reviewNote,
      });

      setSelectedLeave(null);
      setReviewNote("");
      await refreshOverview();
      onNotify?.({
        type: "success",
        text:
          decision === "approved"
            ? "Leave request approved successfully."
            : "Leave request rejected successfully.",
      });
    } catch (error) {
      onNotify?.({
        type: "error",
        text: error.message,
      });
      onProtectedError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleImportLeaves(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsSubmitting(true);
      const rows = parseLeavesCsv(await file.text());

      if (!rows.length) {
        onNotify?.({
          type: "warning",
          text: "Import file is empty or not in the expected format.",
        });
        return;
      }

      const payload = await importAdminLeaves(token, rows);
      await refreshOverview();
      onNotify?.({
        type: "success",
        text: payload.message || "Leaves imported successfully.",
      });
    } catch (error) {
      onNotify?.({
        type: "error",
        text: error.message,
      });
      onProtectedError?.(error);
    } finally {
      setIsSubmitting(false);
      event.target.value = "";
    }
  }

  function handleExportReport() {
    const rows = [
      ["Employee", "Leave Type", "Duration", "From", "To", "Total Days", "Status", "Applied On"],
      ...requests.map((leave) => [
        `${leave.fullName} (${leave.employeeId})`,
        leave.leaveTypeLabel,
        leave.durationLabel,
        leave.fromDateKey || leave.fromDateLabel,
        leave.toDateKey || leave.toDateLabel,
        leave.totalDays,
        getLeaveStatusMeta(leave.status).label,
        String(leave.appliedOn || "").slice(0, 10) || leave.appliedOnLabel,
      ]),
    ];

    downloadLeavesCsv(`instateam-leaves-${monthKey}.csv`, rows);
  }

  return (
    <section className="attendance-page leaves-page">
      <div className="attendance-page-actions">
        <button
          className="secondary-button"
          type="button"
          onClick={() => setShowPolicyModal(true)}
        >
          <AppIcon name="documents" />
          Leave Policy
        </button>
        <button
          className="secondary-button"
          type="button"
          onClick={() => fileInputRef.current?.click()}
        >
          <AppIcon name="upload" />
          Import Leaves
        </button>
        <button className="primary-button" type="button" onClick={openApplyLeaveModal}>
          <AppIcon name="plus" />
          Apply Leave For Employee
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          hidden
          onChange={handleImportLeaves}
        />
      </div>

      <div className="metrics-grid attendance-metrics-grid">
        <MetricCard
          label="Total Leave Requests"
          value={metrics.totalRequests}
          helper="All time"
          icon="calendar"
        />
        <MetricCard
          label="Pending Requests"
          value={metrics.pendingRequests}
          helper="Awaiting approval"
          icon="attendance"
        />
        <MetricCard
          label="Approved Requests"
          value={metrics.approvedRequests}
          helper="All time"
          icon="employees"
          tone="success"
        />
        <MetricCard
          label="Rejected Requests"
          value={metrics.rejectedRequests}
          helper="All time"
          icon="complaints"
          tone="danger"
        />
        <MetricCard
          label="Employees on Leave"
          value={metrics.employeesOnLeaveToday}
          helper="Today"
          icon="activeUsers"
        />
      </div>

      <div className="attendance-main-grid leaves-main-grid">
        <div className="attendance-main-column">
          <article className="table-card leaves-table-card">
            <div className="leaves-tabs-row">
              <button
                type="button"
                className={statusFilter === "all" ? "active" : ""}
                onClick={() => setStatusFilter("all")}
              >
                All Requests
              </button>
              <button
                type="button"
                className={statusFilter === "pending" ? "active" : ""}
                onClick={() => setStatusFilter("pending")}
              >
                Pending ({statusBreakdown.pendingRequests || 0})
              </button>
              <button
                type="button"
                className={statusFilter === "approved" ? "active" : ""}
                onClick={() => setStatusFilter("approved")}
              >
                Approved ({statusBreakdown.approvedRequests || 0})
              </button>
              <button
                type="button"
                className={statusFilter === "rejected" ? "active" : ""}
                onClick={() => setStatusFilter("rejected")}
              >
                Rejected ({statusBreakdown.rejectedRequests || 0})
              </button>
            </div>

            <div className="attendance-toolbar-actions leaves-filter-row">
              <div className="attendance-inline-select">
                <select
                  value={departmentFilter}
                  onChange={(event) => setDepartmentFilter(event.target.value)}
                >
                  <option value="all">All Departments</option>
                  {departmentOptions.map((department) => (
                    <option key={department} value={department}>
                      {department}
                    </option>
                  ))}
                </select>
                <AppIcon name="chevronDown" />
              </div>

              <div className="attendance-inline-select">
                <select
                  value={leaveTypeFilter}
                  onChange={(event) => setLeaveTypeFilter(event.target.value)}
                >
                  <option value="all">All Leave Types</option>
                  {leaveTypes.map((leaveType) => (
                    <option key={leaveType.key} value={leaveType.key}>
                      {leaveType.label}
                    </option>
                  ))}
                </select>
                <AppIcon name="chevronDown" />
              </div>

              <div className="attendance-inline-select">
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option value="all">All Status</option>
                  {ADMIN_LEAVE_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <AppIcon name="chevronDown" />
              </div>

              <label className="attendance-date-chip leaves-month-chip">
                <AppIcon name="calendar" />
                <input
                  type="month"
                  value={monthKey}
                  onChange={(event) => setMonthKey(event.target.value || getCurrentMonthKey())}
                />
              </label>

              <button className="secondary-button" type="button" onClick={() => refreshOverview()}>
                <AppIcon name="filter" />
                Filter
              </button>
            </div>

            {isLoading ? (
              <div className="employee-attendance-loading">Loading leave requests...</div>
            ) : requests.length ? (
              <>
                <div className="leaves-data-table">
                  <div className="leaves-data-header">
                    <span>Employee</span>
                    <span>Leave Type</span>
                    <span>Duration</span>
                    <span>From</span>
                    <span>To</span>
                    <span>Total Days</span>
                    <span>Status</span>
                    <span>Applied On</span>
                    <span>Action</span>
                  </div>

                  {paginatedRequests.map((leave) => {
                    const typeMeta = getAdminLeaveTypeMeta(leave.leaveType);
                    const statusMeta = getLeaveStatusMeta(leave.status);

                    return (
                      <div key={leave._id} className="leaves-data-row">
                        <div className="attendance-person-cell">
                          <Avatar leave={leave} />
                          <div className="attendance-request-copy">
                            <strong>{leave.fullName}</strong>
                            <span>{leave.designation}</span>
                          </div>
                        </div>

                        <div className="leaves-type-cell">
                          <span className={`attendance-quick-icon ${typeMeta.accent || "warning"}`}>
                            <AppIcon name={typeMeta.icon} />
                          </span>
                          <strong>{leave.leaveTypeLabel}</strong>
                        </div>

                        <span>{leave.durationLabel}</span>
                        <span>{leave.fromDateDayLabel}</span>
                        <span>{leave.toDateDayLabel}</span>
                        <span>{formatLeaveDays(leave.totalDays)} Day</span>
                        <span className={`status-badge ${statusMeta.tone}`}>{statusMeta.label}</span>
                        <span>{leave.appliedOnLabel}</span>
                        <button
                          className="icon-button attendance-edit-button"
                          type="button"
                          onClick={() => openReviewModal(leave)}
                        >
                          <AppIcon name="more" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="department-footer-actions leaves-footer-row">
                  <span>
                    Showing {(safePage - 1) * pageSize + 1} to{" "}
                    {Math.min(safePage * pageSize, requests.length)} of {requests.length} requests
                  </span>

                  <div className="employees-pagination">
                    <button
                      className="page-arrow"
                      type="button"
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={safePage === 1}
                    >
                      <AppIcon name="chevronLeft" />
                    </button>

                    <div className="page-numbers">
                      {Array.from({ length: totalPages }, (_, index) => index + 1)
                        .slice(0, 5)
                        .map((page) => (
                          <button
                            key={page}
                            className={`page-number ${safePage === page ? "active" : ""}`}
                            type="button"
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        ))}
                    </div>

                    <button
                      className="page-arrow"
                      type="button"
                      onClick={() =>
                        setCurrentPage((page) => Math.min(totalPages, page + 1))
                      }
                      disabled={safePage === totalPages}
                    >
                      <AppIcon name="chevronRight" />
                    </button>

                    <div className="page-size-shell">
                      <select
                        value={pageSize}
                        onChange={(event) => setPageSize(Number(event.target.value))}
                      >
                        {PAGE_SIZE_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option} / page
                          </option>
                        ))}
                      </select>
                      <AppIcon name="chevronDown" />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="employee-attendance-empty">
                No leave requests found for the selected filters.
              </div>
            )}
          </article>
        </div>

        <div className="attendance-side-column">
          <article className="table-card attendance-summary-card">
            <div className="attendance-side-heading">
              <h3>Leave Summary ({formatMonthHeading(monthKey)})</h3>
            </div>

            <div className="attendance-summary-chart">
              <div className="attendance-summary-donut" style={donutStyle}>
                <div className="attendance-summary-donut-center">
                  <strong>{metrics.totalRequests}</strong>
                  <span>Total Requests</span>
                </div>
              </div>

              <div className="attendance-summary-list">
                <div className="attendance-summary-list-item">
                  <span>
                    <i className="attendance-dot success" />
                    Approved
                  </span>
                  <strong>{metrics.approvedRequests}</strong>
                </div>
                <div className="attendance-summary-list-item">
                  <span>
                    <i className="attendance-dot warning" />
                    Pending
                  </span>
                  <strong>{metrics.pendingRequests}</strong>
                </div>
                <div className="attendance-summary-list-item">
                  <span>
                    <i className="attendance-dot danger" />
                    Rejected
                  </span>
                  <strong>{metrics.rejectedRequests}</strong>
                </div>
              </div>
            </div>
          </article>

          <article className="table-card leaves-type-summary-card">
            <div className="attendance-side-heading">
              <h3>Leave Type Summary</h3>
            </div>

            <div className="leaves-type-summary-list">
              {leaveTypeSummary.length ? (
                leaveTypeSummary.map((item) => (
                  <div key={item.key} className="leaves-type-summary-item">
                    <div className="leaves-type-summary-copy">
                      <strong>{item.label}</strong>
                      <span>{item.requestCount} request(s)</span>
                    </div>
                    <strong>{item.percentage}%</strong>
                    <div className="leaves-type-summary-bar">
                      <span style={{ width: `${Math.min(item.percentage, 100)}%` }} />
                    </div>
                  </div>
                ))
              ) : (
                <div className="employee-attendance-empty">
                  No leave type summary available yet.
                </div>
              )}
            </div>
          </article>

          <article className="table-card attendance-quick-actions-card">
            <div className="attendance-side-heading">
              <h3>Quick Actions</h3>
            </div>

            <div className="attendance-quick-actions-grid">
              <button
                className="attendance-quick-action"
                type="button"
                onClick={() => setStatusFilter("pending")}
              >
                <span className="attendance-quick-icon warning">
                  <AppIcon name="attendance" />
                </span>
                <span>Pending List</span>
              </button>

              <button
                className="attendance-quick-action"
                type="button"
                onClick={() => setStatusFilter("rejected")}
              >
                <span className="attendance-quick-icon danger">
                  <AppIcon name="complaints" />
                </span>
                <span>Rejected List</span>
              </button>

              <button
                className="attendance-quick-action"
                type="button"
                onClick={openApplyLeaveModal}
              >
                <span className="attendance-quick-icon info">
                  <AppIcon name="employees" />
                </span>
                <span>Apply Employee Leave</span>
              </button>

              <button
                className="attendance-quick-action"
                type="button"
                onClick={handleExportReport}
              >
                <span className="attendance-quick-icon violet">
                  <AppIcon name="export" />
                </span>
                <span>Export Report</span>
              </button>
            </div>
          </article>

          <article className="table-card attendance-requests-card">
            <div className="attendance-card-row">
              <div className="attendance-side-heading">
                <h3>Pending Requests</h3>
              </div>
              <button
                className="secondary-button attendance-header-button"
                type="button"
                onClick={() => setStatusFilter("pending")}
              >
                View All
              </button>
            </div>

            <div className="attendance-requests-list">
              {pendingRequests.length ? (
                pendingRequests.map((leave) => (
                  <button
                    key={leave._id}
                    className="attendance-request-item leave-request-item"
                    type="button"
                    onClick={() => openReviewModal(leave)}
                  >
                    <div className="attendance-request-person">
                      <Avatar leave={leave} />
                      <div className="attendance-request-copy">
                        <strong>{leave.fullName}</strong>
                        <span>{leave.designation}</span>
                      </div>
                    </div>

                    <div className="attendance-request-actions">
                      <div className="attendance-request-copy">
                        <strong>{leave.fromDateLabel}</strong>
                        <span>{leave.leaveTypeLabel}</span>
                      </div>
                      <span className="status-badge warning">Pending</span>
                    </div>
                  </button>
                ))
              ) : (
                <div className="employee-attendance-empty">
                  No pending leave requests right now.
                </div>
              )}
            </div>
          </article>
        </div>
      </div>

      {showPolicyModal ? (
        <LeavePolicyModal
          policy={overview?.policy}
          onClose={() => setShowPolicyModal(false)}
        />
      ) : null}

      {showApplyModal ? (
        <ApplyLeaveModal
          employees={activeEmployees}
          leaveTypes={leaveTypes}
          form={applyForm}
          onChange={setApplyForm}
          onClose={() => setShowApplyModal(false)}
          onSubmit={handleSubmitApplyLeave}
          isSubmitting={isSubmitting}
        />
      ) : null}

      {selectedLeave ? (
        <ReviewLeaveModal
          leave={selectedLeave}
          reviewNote={reviewNote}
          onReviewNoteChange={setReviewNote}
          onApprove={() => handleReviewLeave("approved")}
          onReject={() => handleReviewLeave("rejected")}
          onClose={() => setSelectedLeave(null)}
          isSubmitting={isSubmitting}
        />
      ) : null}
    </section>
  );
}
